import { Button, Flex, Typography, colors } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import {
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
} from '@aloft/toolkit-sui/chakra';

import { useEffect } from 'react';

import Teacher from '../../assets/sam/teacher.png';
import {
  selectActiveTour,
  selectActiveTourIndex,
} from '../../redux/features/selectors/uiSelectors.ts';
import {
  decrementActiveTourIndex,
  incrementActiveTourIndex,
  resetActiveTourIndex,
  setActiveTour,
  tourLengths,
} from '../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { ActiveTour } from '../../redux/types.ts';
import { GuidedTourTooltip } from './GuidedTourTooltip.tsx';
import { GuidedTourTrigger } from './GuidedTourTrigger.tsx';
import { tasks } from './consts.ts';

export const GuidedTourPopover = ({
  tourID,
  ftux,
  dismiss,
}: {
  tourID: ActiveTour;
  ftux: () => boolean;
  dismiss: () => void;
}) => {
  const dispatch = useAppDispatch();
  const activeTour = useAppSelector(selectActiveTour);
  const activeTourIndex = useAppSelector(selectActiveTourIndex);

  useEffect(() => {
    if (ftux()) {
      dispatch(setActiveTour({ activeTour: tourID }));
    }
  }, [dispatch, ftux, tourID]);

  const onClose = () => {
    if (activeTour && activeTourIndex === tourLengths[activeTour] - 1) {
      dispatch(resetActiveTourIndex());
    }
    dispatch(setActiveTour({ activeTour: null }));
    dismiss();
  };

  const isOpen = tourID === activeTour;

  const activeTasks = activeTour ? tasks[activeTour] : { items: [], tooltipMapping: [] };

  return (
    <Popover isOpen={isOpen} onClose={onClose} closeOnEsc={false} closeOnBlur={false}>
      <PopoverTrigger>
        <GuidedTourTrigger tourID="analysisTour" />
      </PopoverTrigger>
      {isOpen && (
        <Portal>
          <PopoverContent
            sx={{
              borderColor: colors.strokeDefault,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '8px',
              padding: '1.25rem',
              bottom: '.5rem',
              right: '.5rem',
              zIndex: -1, // float below tooltips if they clash
              filter: 'drop-shadow(0px 0px 8px #bbbbbb)',
            }}
          >
            <PopoverCloseButton onClick={onClose} />
            <PopoverHeader>
              <GuidedTourTooltip
                tourID="analysisTour"
                tourIndex={13}
                text={[
                  'YOU MADE IT!',
                  'Thank you for taking the tour!',
                  'For more information you can access the page guide.',
                ]}
              >
                <img src={Teacher} alt="Teacher" width="82" />
              </GuidedTourTooltip>
              <Typography variant="h3">Tasks to complete analysis</Typography>
            </PopoverHeader>
            <PopoverBody>
              {activeTasks.items.map((text, idx) => (
                <Typography
                  key={`${text}-${idx}`}
                  __dangerDoNotUseSx={{
                    fontWeight: activeTasks.tooltipMapping[activeTourIndex] === idx ? '700' : '400',
                  }}
                >{`${idx + 1}.\u00A0\u00A0\u00A0\u00A0${text}`}</Typography>
              ))}
              <Flex gap=".5rem" marginTop="1rem">
                {activeTourIndex === -1 && (
                  <Button
                    size="sm"
                    variant="primary"
                    text="Take a tour"
                    onClick={() => dispatch(incrementActiveTourIndex())}
                  />
                )}
                {activeTourIndex > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    text="Back"
                    onClick={() => dispatch(decrementActiveTourIndex())}
                  />
                )}
                {activeTour &&
                  activeTourIndex < tourLengths[activeTour] - 1 &&
                  activeTourIndex !== -1 && (
                    <Button
                      size="sm"
                      variant="primary"
                      text="Next"
                      onClick={() => dispatch(incrementActiveTourIndex())}
                    />
                  )}
                {activeTour && activeTourIndex === tourLengths[activeTour] - 1 && (
                  <Button size="sm" variant="primary" text="Done" onClick={onClose} />
                )}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
};
